/**
 * Reports Engine - Centralized calculation and report generation
 * 
 * This module provides pure functions for generating reports dynamically.
 * All calculations are done in memory without database persistence.
 */

import { format, subMonths, startOfMonth, endOfMonth, getDaysInMonth, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ============= Types =============

export interface ReportFilters {
  startDate: Date;
  endDate: Date;
  clientIds?: string[];
  projectIds?: string[];
  billingTypes?: ('hourly' | 'fixed')[];
  categories?: string[];
  paymentStatus?: ('paid' | 'pending' | 'overdue')[];
}

export interface FinancialSummary {
  confirmedRevenue: number;
  expectedRevenue: number;
  totalRevenue: number;
  realHourlyRate: number;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billablePercentage: number;
}

export interface ProjectReport {
  id: string;
  name: string;
  client: string;
  billingType: 'hourly' | 'fixed';
  status: string;
  hoursSpent: number;
  estimatedHours: number;
  revenue: number;
  effectiveRate: number;
  margin: number;
  marginPercent: number;
  targetRate: number;
  isProfitable: boolean;
}

export interface ClientReport {
  id: string;
  name: string;
  totalRevenue: number;
  totalHours: number;
  projectCount: number;
  avgProjectValue: number;
  effectiveRate: number;
  revenueShare: number;
  isHighDependency: boolean;
  paymentReliability: number;
}

export interface TimeReport {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billablePercentage: number;
  hoursByCategory: Record<string, number>;
  hoursByWeekday: Record<string, number>;
  avgHoursPerDay: number;
  peakDay: string;
  peakHour: number;
}

export interface PredictiveAnalysis {
  avgMonthlyRevenue: number;
  growthTrend: number;
  projectedNextMonth: number;
  confidence: 'low' | 'medium' | 'high';
  monthsAnalyzed: number;
}

export interface ExecutiveSummary {
  currentMonthRevenue: number;
  previousMonthRevenue: number;
  growthPercent: number;
  topClient: { name: string; revenue: number } | null;
  topProject: { name: string; revenue: number; rate: number } | null;
  realHourlyRate: number;
  projectedNextMonth: number;
  insights: string[];
}

export interface MonthlyComparison {
  currentRevenue: number;
  previousRevenue: number;
  revenueGrowth: number;
  currentHours: number;
  previousHours: number;
  hoursGrowth: number;
  currentProjects: number;
  previousProjects: number;
}

// ============= Calculation Functions =============

/**
 * Calculate financial summary from payments and time entries
 */
export function calculateFinancialSummary(
  payments: Array<{ amount: number; status: string }>,
  timeEntries: Array<{ duration_minutes: number; is_billable: boolean; hourly_rate?: number }>,
  targetRate: number
): FinancialSummary {
  const confirmedRevenue = payments
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const expectedRevenue = payments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const totalHours = timeEntries.reduce((sum, e) => sum + ((e.duration_minutes || 0) / 60), 0);
  const billableHours = timeEntries
    .filter(e => e.is_billable !== false)
    .reduce((sum, e) => sum + ((e.duration_minutes || 0) / 60), 0);
  const nonBillableHours = totalHours - billableHours;

  const totalRevenue = confirmedRevenue + expectedRevenue;
  const realHourlyRate = billableHours > 0 ? confirmedRevenue / billableHours : 0;

  return {
    confirmedRevenue: Math.round(confirmedRevenue * 100) / 100,
    expectedRevenue: Math.round(expectedRevenue * 100) / 100,
    totalRevenue: Math.round(totalRevenue * 100) / 100,
    realHourlyRate: Math.round(realHourlyRate * 100) / 100,
    totalHours: Math.round(totalHours * 10) / 10,
    billableHours: Math.round(billableHours * 10) / 10,
    nonBillableHours: Math.round(nonBillableHours * 10) / 10,
    billablePercentage: totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : 0,
  };
}

/**
 * Calculate project profitability reports
 */
export function calculateProjectReports(
  projects: Array<{
    id: string;
    name: string;
    client_name?: string;
    billing_type: string;
    status: string;
    fixed_price?: number;
    hourly_rate?: number;
    estimated_hours?: number;
  }>,
  timeEntriesByProject: Record<string, number>, // project_id -> total_hours
  paymentsByProject: Record<string, number>, // project_id -> paid_amount
  targetRate: number
): ProjectReport[] {
  return projects.map(project => {
    const hoursSpent = timeEntriesByProject[project.id] || 0;
    const revenue = paymentsByProject[project.id] || 0;
    const estimatedHours = project.estimated_hours || 0;
    
    let effectiveRate = 0;
    if (project.billing_type === 'hourly') {
      effectiveRate = project.hourly_rate || targetRate;
    } else {
      effectiveRate = hoursSpent > 0 ? revenue / hoursSpent : 0;
    }

    const expectedRevenue = project.billing_type === 'fixed' 
      ? (project.fixed_price || 0)
      : hoursSpent * (project.hourly_rate || targetRate);
    
    const margin = revenue - (hoursSpent * targetRate);
    const marginPercent = hoursSpent > 0 && targetRate > 0
      ? ((effectiveRate - targetRate) / targetRate) * 100
      : 0;

    return {
      id: project.id,
      name: project.name,
      client: project.client_name || 'Sem Cliente',
      billingType: project.billing_type as 'hourly' | 'fixed',
      status: project.status,
      hoursSpent: Math.round(hoursSpent * 10) / 10,
      estimatedHours,
      revenue: Math.round(revenue * 100) / 100,
      effectiveRate: Math.round(effectiveRate * 100) / 100,
      margin: Math.round(margin * 100) / 100,
      marginPercent: Math.round(marginPercent),
      targetRate,
      isProfitable: effectiveRate >= targetRate,
    };
  });
}

/**
 * Calculate client revenue reports
 */
export function calculateClientReports(
  clients: Array<{ id: string; name: string }>,
  projectsByClient: Record<string, Array<{ id: string }>>,
  hoursByClient: Record<string, number>,
  revenueByClient: Record<string, number>,
  totalRevenue: number
): ClientReport[] {
  return clients.map(client => {
    const revenue = revenueByClient[client.id] || 0;
    const hours = hoursByClient[client.id] || 0;
    const projects = projectsByClient[client.id] || [];
    const projectCount = projects.length;
    
    const avgProjectValue = projectCount > 0 ? revenue / projectCount : 0;
    const effectiveRate = hours > 0 ? revenue / hours : 0;
    const revenueShare = totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0;

    return {
      id: client.id,
      name: client.name,
      totalRevenue: Math.round(revenue * 100) / 100,
      totalHours: Math.round(hours * 10) / 10,
      projectCount,
      avgProjectValue: Math.round(avgProjectValue * 100) / 100,
      effectiveRate: Math.round(effectiveRate * 100) / 100,
      revenueShare: Math.round(revenueShare),
      isHighDependency: revenueShare > 50,
      paymentReliability: 100, // Can be calculated from payment history
    };
  }).sort((a, b) => b.totalRevenue - a.totalRevenue);
}

/**
 * Calculate time tracking report with heatmap data
 */
export function calculateTimeReport(
  timeEntries: Array<{
    duration_minutes: number;
    is_billable: boolean;
    category?: string;
    start_time: string;
  }>,
  daysInPeriod: number
): TimeReport {
  const totalMinutes = timeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  const billableMinutes = timeEntries
    .filter(e => e.is_billable !== false)
    .reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  
  const totalHours = totalMinutes / 60;
  const billableHours = billableMinutes / 60;
  const nonBillableHours = totalHours - billableHours;

  // Hours by category
  const hoursByCategory: Record<string, number> = {};
  timeEntries.forEach(e => {
    const cat = e.category || 'other';
    hoursByCategory[cat] = (hoursByCategory[cat] || 0) + ((e.duration_minutes || 0) / 60);
  });

  // Hours by weekday
  const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const hoursByWeekday: Record<string, number> = {};
  weekdays.forEach(d => hoursByWeekday[d] = 0);
  
  timeEntries.forEach(e => {
    const date = new Date(e.start_time);
    const day = weekdays[date.getDay()];
    hoursByWeekday[day] += (e.duration_minutes || 0) / 60;
  });

  // Find peak day and hour
  let peakDay = 'Seg';
  let maxDayHours = 0;
  Object.entries(hoursByWeekday).forEach(([day, hours]) => {
    if (hours > maxDayHours) {
      maxDayHours = hours;
      peakDay = day;
    }
  });

  // Calculate peak hour
  const hourDistribution: Record<number, number> = {};
  timeEntries.forEach(e => {
    const hour = new Date(e.start_time).getHours();
    hourDistribution[hour] = (hourDistribution[hour] || 0) + (e.duration_minutes || 0);
  });
  
  let peakHour = 9;
  let maxHourMinutes = 0;
  Object.entries(hourDistribution).forEach(([hour, minutes]) => {
    if (minutes > maxHourMinutes) {
      maxHourMinutes = minutes;
      peakHour = parseInt(hour);
    }
  });

  return {
    totalHours: Math.round(totalHours * 10) / 10,
    billableHours: Math.round(billableHours * 10) / 10,
    nonBillableHours: Math.round(nonBillableHours * 10) / 10,
    billablePercentage: totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : 0,
    hoursByCategory: Object.fromEntries(
      Object.entries(hoursByCategory).map(([k, v]) => [k, Math.round(v * 10) / 10])
    ),
    hoursByWeekday: Object.fromEntries(
      Object.entries(hoursByWeekday).map(([k, v]) => [k, Math.round(v * 10) / 10])
    ),
    avgHoursPerDay: daysInPeriod > 0 ? Math.round((totalHours / daysInPeriod) * 10) / 10 : 0,
    peakDay,
    peakHour,
  };
}

/**
 * Calculate predictive analysis based on historical data
 */
export function calculatePredictiveAnalysis(
  monthlyRevenues: Array<{ month: Date; revenue: number }>
): PredictiveAnalysis {
  if (monthlyRevenues.length === 0) {
    return {
      avgMonthlyRevenue: 0,
      growthTrend: 0,
      projectedNextMonth: 0,
      confidence: 'low',
      monthsAnalyzed: 0,
    };
  }

  // Sort by date
  const sorted = [...monthlyRevenues].sort((a, b) => a.month.getTime() - b.month.getTime());
  const last3Months = sorted.slice(-3);
  
  const avgMonthlyRevenue = last3Months.reduce((sum, m) => sum + m.revenue, 0) / last3Months.length;

  // Calculate growth trend
  let growthTrend = 0;
  if (last3Months.length >= 2) {
    const growths: number[] = [];
    for (let i = 1; i < last3Months.length; i++) {
      if (last3Months[i - 1].revenue > 0) {
        growths.push(
          ((last3Months[i].revenue - last3Months[i - 1].revenue) / last3Months[i - 1].revenue) * 100
        );
      }
    }
    if (growths.length > 0) {
      growthTrend = growths.reduce((a, b) => a + b, 0) / growths.length;
    }
  }

  // Project next month
  const projectedNextMonth = avgMonthlyRevenue * (1 + growthTrend / 100);

  // Determine confidence
  let confidence: 'low' | 'medium' | 'high' = 'low';
  if (last3Months.length >= 3) {
    confidence = 'high';
  } else if (last3Months.length >= 2) {
    confidence = 'medium';
  }

  return {
    avgMonthlyRevenue: Math.round(avgMonthlyRevenue * 100) / 100,
    growthTrend: Math.round(growthTrend * 10) / 10,
    projectedNextMonth: Math.round(projectedNextMonth * 100) / 100,
    confidence,
    monthsAnalyzed: last3Months.length,
  };
}

/**
 * Generate monthly comparison data
 */
export function calculateMonthlyComparison(
  currentMonth: { revenue: number; hours: number; projects: number },
  previousMonth: { revenue: number; hours: number; projects: number }
): MonthlyComparison {
  const revenueGrowth = previousMonth.revenue > 0
    ? ((currentMonth.revenue - previousMonth.revenue) / previousMonth.revenue) * 100
    : currentMonth.revenue > 0 ? 100 : 0;

  const hoursGrowth = previousMonth.hours > 0
    ? ((currentMonth.hours - previousMonth.hours) / previousMonth.hours) * 100
    : currentMonth.hours > 0 ? 100 : 0;

  return {
    currentRevenue: currentMonth.revenue,
    previousRevenue: previousMonth.revenue,
    revenueGrowth: Math.round(revenueGrowth),
    currentHours: currentMonth.hours,
    previousHours: previousMonth.hours,
    hoursGrowth: Math.round(hoursGrowth),
    currentProjects: currentMonth.projects,
    previousProjects: previousMonth.projects,
  };
}

/**
 * Generate executive summary with insights
 */
export function generateExecutiveSummary(
  currentMonthRevenue: number,
  previousMonthRevenue: number,
  clients: Array<{ name: string; revenue: number }>,
  projects: Array<{ name: string; revenue: number; rate: number }>,
  realHourlyRate: number,
  targetRate: number,
  predictive: PredictiveAnalysis
): ExecutiveSummary {
  const growthPercent = previousMonthRevenue > 0
    ? ((currentMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100
    : currentMonthRevenue > 0 ? 100 : 0;

  const topClient = clients.length > 0
    ? clients.reduce((max, c) => c.revenue > max.revenue ? c : max, clients[0])
    : null;

  const profitableProjects = projects.filter(p => p.rate >= targetRate);
  const topProject = profitableProjects.length > 0
    ? profitableProjects.reduce((max, p) => p.rate > max.rate ? p : max, profitableProjects[0])
    : projects.length > 0 
      ? projects.reduce((max, p) => p.revenue > max.revenue ? p : max, projects[0])
      : null;

  // Generate insights
  const insights: string[] = [];

  if (growthPercent > 10) {
    insights.push(`Crescimento de ${Math.round(growthPercent)}% em relação ao mês anterior.`);
  } else if (growthPercent < -10) {
    insights.push(`Queda de ${Math.abs(Math.round(growthPercent))}% em relação ao mês anterior.`);
  }

  if (realHourlyRate < targetRate * 0.9) {
    insights.push(`Valor/hora real está ${Math.round((1 - realHourlyRate / targetRate) * 100)}% abaixo da meta.`);
  } else if (realHourlyRate > targetRate * 1.1) {
    insights.push(`Valor/hora real está ${Math.round((realHourlyRate / targetRate - 1) * 100)}% acima da meta.`);
  }

  if (topClient && clients.length > 1) {
    const totalRevenue = clients.reduce((sum, c) => sum + c.revenue, 0);
    const share = totalRevenue > 0 ? (topClient.revenue / totalRevenue) * 100 : 0;
    if (share > 60) {
      insights.push(`Atenção: ${topClient.name} representa ${Math.round(share)}% da receita.`);
    }
  }

  if (predictive.confidence === 'high' && predictive.growthTrend > 5) {
    insights.push(`Tendência de crescimento: +${Math.round(predictive.growthTrend)}% mensal.`);
  }

  return {
    currentMonthRevenue: Math.round(currentMonthRevenue * 100) / 100,
    previousMonthRevenue: Math.round(previousMonthRevenue * 100) / 100,
    growthPercent: Math.round(growthPercent),
    topClient,
    topProject,
    realHourlyRate: Math.round(realHourlyRate * 100) / 100,
    projectedNextMonth: predictive.projectedNextMonth,
    insights,
  };
}

// ============= Export Utilities =============

export interface ExportData {
  summary: FinancialSummary;
  projects: ProjectReport[];
  clients: ClientReport[];
  timeReport: TimeReport;
  period: { start: string; end: string };
  generatedAt: string;
  userName?: string;
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format currency in short form
 */
export function formatCurrencyShort(value: number): string {
  if (value >= 1000000) {
    return `R$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `R$${(value / 1000).toFixed(1)}k`;
  }
  return `R$${value.toFixed(0)}`;
}

/**
 * Format percentage
 */
export function formatPercent(value: number): string {
  return `${value >= 0 ? '+' : ''}${value}%`;
}

/**
 * Get category label in Portuguese
 */
export function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    dev: 'Desenvolvimento',
    meeting: 'Reunião',
    research: 'Pesquisa',
    admin: 'Administrativo',
    other: 'Outros',
  };
  return labels[category] || category;
}

/**
 * Get status label in Portuguese
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    backlog: 'Backlog',
    in_progress: 'Em Andamento',
    review: 'Revisão',
    done: 'Concluído',
  };
  return labels[status] || status;
}
